/**
 * Sentry Error Tracking Integration
 * 
 * Production-ready wrapper for Sentry React SDK with GDPR compliance.
 * 
 * Features:
 * - Error tracking with PII scrubbing
 * - Performance monitoring
 * - Source map support
 * - Privacy-safe error reporting
 * - React error boundary
 */

import * as Sentry from '@sentry/react';
import { hashUserId, sanitizeError, getAppVersion, getPlatform } from '../../utils/telemetry/pii-redaction';

let isInitialized = false;

/**
 * Get analytics consent from user settings
 * 
 * Sentry respects same consent as PostHog.
 * 
 * @returns true if user has explicitly opted in
 */
function getAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  
  const localConsent = localStorage.getItem('analytics_consent');
  if (localConsent === 'false') return false;
  if (localConsent === 'true') return true;
  
  // Default: false (explicit opt-in required)
  return false;
}

/**
 * Initialize Sentry SDK
 * 
 * Should be called early in app lifecycle, before React renders.
 * Will NOT initialize if user has not consented (GDPR compliance).
 */
export function initializeSentry(): void {
  if (typeof window === 'undefined') {
    console.log('[Sentry] Skipping initialization (no window object)');
    return;
  }
  
  if (isInitialized) {
    console.log('[Sentry] Already initialized');
    return;
  }
  
  // In development, log that Sentry is initializing in dev mode
  if (import.meta.env.DEV) {
    console.log('[Sentry] Initializing in development mode');
  }
  
  // Check user consent
  const consentGiven = getAnalyticsConsent();
  if (!consentGiven) {
    console.log('[Sentry] User has not opted in to error tracking - initialization skipped');
    return;
  }
  
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || dsn === 'undefined' || dsn === '') {
    console.warn('[Sentry] VITE_SENTRY_DSN not configured - error tracking disabled');
    return;
  }
  
  try {
    console.log('[Sentry] Initializing for environment:', import.meta.env.MODE);
    
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE || 'production',
      release: `loop-orienteering@${getAppVersion()}`,
      
      integrations: [
        // Browser tracing for performance monitoring
        Sentry.browserTracingIntegration({
          // Disable automatic instrumentation for privacy
          traceFetch: false,
          traceXHR: false,
        }),
      ],
      
      // Performance monitoring (10% sample rate)
      tracesSampleRate: 0.1,
      
      // Error sampling (100% for beta)
      sampleRate: 1.0,
      
      // PII scrubbing
      beforeSend(event, hint) {
        // Remove PII from error messages
        if (event.message) {
          event.message = sanitizeError(event.message);
        }
        
        // Remove PII from exception values
        if (event.exception?.values) {
          event.exception.values = event.exception.values.map(exception => ({
            ...exception,
            value: exception.value ? sanitizeError(exception.value) : exception.value,
          }));
        }
        
        // Remove tokens from headers
        if (event.request?.headers) {
          const headers = { ...event.request.headers };
          delete headers.Authorization;
          delete headers.authorization;
          event.request.headers = headers;
        }
        
        // Remove query parameters from URLs (may contain tokens)
        if (event.request?.url) {
          try {
            const url = new URL(event.request.url);
            event.request.url = `${url.origin}${url.pathname}`;
          } catch {
            // Invalid URL, keep as is
          }
        }
        
        return event;
      },
      
      // Ignore known non-critical errors
      ignoreErrors: [
        // Browser extensions
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // Promise rejections without proper Error objects
        'Non-Error promise rejection captured',
        // Network errors (too noisy, track via API layer instead)
        'Network request failed',
        'NetworkError',
        'Failed to fetch',
        // Ionic/Capacitor known issues
        'Cannot read properties of undefined (reading \'addEventListener\')',
      ],
      
      // Ignore errors from browser extensions
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
      ],
    });
    
    isInitialized = true;
    console.log('[Sentry] Initialized successfully:', {
      environment: import.meta.env.MODE,
      release: `loop-orienteering@${getAppVersion()}`,
    });
  } catch (error) {
    console.error('[Sentry] Initialization failed:', error);
  }
}

/**
 * Set user context for Sentry
 * 
 * Uses HMAC-SHA256 hashed user ID for privacy.
 * 
 * @param userId - Supabase user ID (will be hashed), or null to clear
 * @param isAdmin - Whether user is admin
 */
export async function setUser(userId: string | null, isAdmin: boolean = false): Promise<void> {
  // Allow clearing user context even if not initialized (GDPR data deletion)
  if (!isInitialized && userId !== null) {
    return;
  }
  
  try {
    if (userId) {
      const hashedUserId = await hashUserId(userId);
      Sentry.setUser({
        id: hashedUserId,
        platform: getPlatform(),
        is_admin: isAdmin,
      });
      
      if (import.meta.env.DEV) {
        console.log('[Sentry] User context set:', hashedUserId.substring(0, 8) + '...');
      }
    } else {
      Sentry.setUser(null);
      
      if (import.meta.env.DEV) {
        console.log('[Sentry] User context cleared');
      }
    }
  } catch (error) {
    console.error('[Sentry] Failed to set user context:', error);
  }
}

/**
 * Manually capture an exception
 * 
 * Use for caught errors that should still be reported to Sentry.
 * 
 * @param error - Error object or message
 * @param context - Additional context (will be validated for PII)
 */
export function captureException(error: Error | string, context?: Record<string, any>): void {
  if (!isInitialized) {
    return;
  }
  
  try {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    
    // Sanitize error message
    errorObj.message = sanitizeError(errorObj.message);
    
    // Capture with context
    Sentry.captureException(errorObj, {
      contexts: context ? { custom: context } : undefined,
    });
    
    if (import.meta.env.DEV) {
      console.error('[Sentry] Exception captured:', errorObj.message);
    }
  } catch (error) {
    console.error('[Sentry] Failed to capture exception:', error);
  }
}

/**
 * Capture a message (non-error)
 * 
 * Use for informational or warning messages.
 * 
 * @param message - Message to capture
 * @param level - Severity level
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  if (!isInitialized) {
    return;
  }
  
  try {
    // Sanitize message
    const sanitizedMessage = sanitizeError(message);
    
    Sentry.captureMessage(sanitizedMessage, level);
    
    if (import.meta.env.DEV) {
      console.log('[Sentry] Message captured:', sanitizedMessage, `[${level}]`);
    }
  } catch (error) {
    console.error('[Sentry] Failed to capture message:', error);
  }
}

/**
 * Start a performance transaction
 * 
 * Use for tracking long-running operations (API calls, loop runs, etc.)
 * 
 * @param name - Transaction name
 * @param op - Operation type
 * @returns Transaction object (call finish() when done)
 */
export function startTransaction(
  name: string,
  op: string = 'custom'
): Sentry.Transaction | null {
  if (!isInitialized) {
    return null;
  }
  
  try {
    return Sentry.startTransaction({
      name,
      op,
    }) as Sentry.Transaction;
  } catch (error) {
    console.error('[Sentry] Failed to start transaction:', error);
    return null;
  }
}

/**
 * Start a performance span (child of transaction)
 * 
 * @param operation - Operation name
 * @param description - Operation description
 * @returns Span object (call finish() when done)
 */
export function startSpan(
  operation: string,
  description?: string
): Sentry.Span | null {
  if (!isInitialized) {
    return null;
  }
  
  try {
    // Use startInactiveSpan which requires only one argument
    return Sentry.startInactiveSpan({
      op: operation,
      name: description || operation,
    });
  } catch (error) {
    console.error('[Sentry] Failed to start span:', error);
    return null;
  }
}

/**
 * Add breadcrumb for debugging context
 * 
 * Breadcrumbs are included with error reports to show what happened before the error.
 * 
 * @param message - Breadcrumb message
 * @param category - Breadcrumb category
 * @param level - Severity level
 */
export function addBreadcrumb(
  message: string,
  category: string = 'custom',
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  if (!isInitialized) {
    return;
  }
  
  try {
    Sentry.addBreadcrumb({
      message: sanitizeError(message),
      category,
      level,
      timestamp: Date.now() / 1000,
    });
  } catch (error) {
    console.error('[Sentry] Failed to add breadcrumb:', error);
  }
}

/**
 * Shutdown Sentry
 * 
 * Called when user opts out of error tracking.
 */
export function shutdownSentry(): void {
  if (!isInitialized) {
    return;
  }
  
  try {
    Sentry.close();
    isInitialized = false;
    console.log('[Sentry] Shutdown complete');
  } catch (error) {
    console.error('[Sentry] Shutdown failed:', error);
  }
}

/**
 * React Error Boundary component
 * 
 * Wrap your app in this component to catch unhandled React errors.
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Get current Sentry status
 * 
 * @returns Object with initialization and consent status
 */
export function getSentryStatus(): {
  initialized: boolean;
  consentGiven: boolean;
} {
  return {
    initialized: isInitialized,
    consentGiven: getAnalyticsConsent(),
  };
}

/**
 * Check if Sentry is ready to capture errors
 * 
 * @returns true if initialized and consent given
 */
export function isSentryReady(): boolean {
  return isInitialized && getAnalyticsConsent();
}

/**
 * Set analytics consent and initialize/shutdown Sentry accordingly
 * 
 * Implements GDPR Article 7(3) compliance: Users can withdraw consent as easily as granting it.
 * This function enables users to toggle analytics on/off from Settings screen.
 * 
 * @param enabled - true to grant consent and initialize, false to revoke consent and shutdown
 */
export function setAnalyticsConsent(enabled: boolean): void {
  if (typeof window === 'undefined') {
    console.warn('[Sentry] Cannot set consent - window object not available');
    return;
  }
  
  // Update localStorage with consent choice
  localStorage.setItem('analytics_consent', enabled ? 'true' : 'false');
  localStorage.setItem('analytics_consent_shown', 'true');
  
  if (enabled) {
    // User granted consent - initialize Sentry
    console.log('[Sentry] Consent granted - initializing');
    initializeSentry();
  } else {
    // User revoked consent - shutdown Sentry
    console.log('[Sentry] Consent revoked - shutting down');
    shutdownSentry();
  }
}
